import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, Copy, ShieldCheck, KeyRound, Mail, Send, RefreshCw, AlertTriangle, TrendingUp, Clock, Building2, Ban, X, CreditCard, FileText, Package, LayoutGrid } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import NewSchoolCard, { PortalPicker } from "@/components/app/NewSchoolCard";
import { portalsOf, modulesOf, PORTALS } from "@/lib/modules";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export default function Admin() {
  const { user, loading } = useAuth();
  const [emails, setEmails] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [insights, setInsights] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [newPlan, setNewPlan] = useState("base");
  const [modsEdit, setModsEdit] = useState(null);
  const [codeNote, setCodeNote] = useState("");
  const [codeSchool, setCodeSchool] = useState("");
  const [codePhone, setCodePhone] = useState("");
  const [codeDays, setCodeDays] = useState(2);
  const [licenseDays, setLicenseDays] = useState(365);
  const [subEdit, setSubEdit] = useState(null);
  const [subPayments, setSubPayments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [contractFor, setContractFor] = useState(null);
  const [savingContract, setSavingContract] = useState(false);
  const [contractHistory, setContractHistory] = useState([]);

  const load = async () => {
    try {
      const [e, c, u, a, ins, sp, pl] = await Promise.all([
        api.get("/admin/allowed-emails"),
        api.get("/admin/invite-codes"),
        api.get("/admin/users"),
        api.get("/admin/access-attempts"),
        api.get("/admin/insights"),
        api.get("/admin/subscription-payments"),
        api.get("/admin/plans"),
      ]);
      setEmails(e.data); setCodes(c.data); setUsers(u.data); setAttempts(a.data); setInsights(ins.data); setSubPayments(sp.data); setPlans(pl.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (user?.role === "owner") load(); }, [user]);

  useEffect(() => {
    if (!contractFor?.user_id) { setContractHistory([]); return; }
    api.get(`/admin/users/${contractFor.user_id}/contracts`)
      .then((r) => setContractHistory(r.data || []))
      .catch(() => setContractHistory([]));
  }, [contractFor?.user_id]);

  if (loading) return null;
  if (!user || user.role !== "owner") return <Navigate to="/dashboard" replace />;

  const addEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    try {
      await api.post("/admin/allowed-emails", { email: newEmail, note: newNote, plan: newPlan });
      toast.success("Correo autorizado con su plan");
      setNewEmail(""); setNewNote(""); setNewPlan("base"); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const removeEmail = (email) => {
    setConfirm({
      title: "Revocar correo autorizado",
      description: `¿Quitar el acceso a ${email}? Este correo ya no podrá iniciar sesión.`,
      confirmLabel: "Revocar",
      onConfirm: async () => {
        try {
          await api.delete(`/admin/allowed-emails/${encodeURIComponent(email)}`);
          toast.success("Correo removido"); load();
        } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
      },
    });
  };

  const runConfirm = async () => {
    const fn = confirm?.onConfirm;
    setConfirm(null);
    if (fn) await fn();
  };

  const createCode = async () => {
    try {
      const { data } = await api.post("/admin/invite-codes", {
        note: codeNote, school_name: codeSchool, phone: codePhone,
        expires_in_hours: Number(codeDays), license_days: Number(licenseDays),
      });
      toast.success(`Código creado: ${data.code}`);
      setCodeNote(""); setCodeSchool(""); setCodePhone(""); load();
    } catch { toast.error("Error"); }
  };

  const extendLicense = async (u) => {
    const daysStr = window.prompt(`¿Cuántos días extender la licencia de ${u.email}?`, "365");
    if (!daysStr) return;
    const days = parseInt(daysStr, 10);
    if (!days || days <= 0) return;
    try {
      await api.post(`/admin/users/${u.user_id}/extend`, { days });
      toast.success(`Licencia extendida +${days} días`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const saveSubscription = async () => {
    if (!subEdit) return;
    const amount = Math.max(0, parseFloat(subEdit.amount) || 0);
    const days = Math.max(1, parseInt(subEdit.days, 10) || 365);
    try {
      await api.post(`/admin/users/${subEdit.user_id}/subscription`, { amount, days });
      toast.success("Precio de suscripción guardado");
      setSubEdit(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const saveModules = async () => {
    try {
      await api.post(`/admin/users/${modsEdit.user_id}/modules`, { modules: modulesOf(modsEdit.portals) });
      toast.success("Portales actualizados"); setModsEdit(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const setUserPlan = async (u, planKey) => {
    if (!planKey) return;
    try {
      await api.post(`/admin/users/${u.user_id}/plan`, { plan: planKey });
      const pn = plans.find((p) => p.key === planKey)?.name || planKey;
      toast.success(`Plan asignado: ${pn}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const downloadContract = async () => {
    if (!contractFor) return;
    if (!contractFor.responsable.trim()) { toast.error("Escribe el nombre del responsable de pago"); return; }
    setSavingContract(true);
    try {
      const res = await api.post(`/admin/users/${contractFor.user_id}/contract`,
        { responsable: contractFor.responsable.trim(), colegio: contractFor.colegio.trim(), plan: contractFor.plan },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Contrato_ZAVIA_${(contractFor.colegio || "colegio").replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Contrato descargado");
      try { const r = await api.get(`/admin/users/${contractFor.user_id}/contracts`); setContractHistory(r.data || []); } catch { /* ignore */ }
    } catch (err) { toast.error("No se pudo generar el contrato"); }
    finally { setSavingContract(false); }
  };

  const redownloadContract = async (c) => {
    try {
      const res = await api.get(`/admin/contracts/${c.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Contrato_ZAVIA_${(c.colegio || "colegio").replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar el contrato"); }
  };

  const exportSubCsv = () => {
    if (!subPayments.length) { toast.info("Aún no hay cobros de suscripción"); return; }
    const rows = [["Fecha", "Colegio", "Correo", "Monto (MXN)", "Días", "Método"]];
    subPayments.forEach((p) => rows.push([
      new Date(p.created_at).toLocaleString("es-MX"),
      p.school_name || "", p.email || "", Number(p.amount || 0).toFixed(2),
      p.days || "", p.method || "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cobros_suscripcion_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const waLink = (phone, code, school) => {
    const cleaned = (phone || "").replace(/[^0-9]/g, "");
    if (!cleaned) return null;
    const msg = encodeURIComponent(`Hola${school ? " " + school : ""}, tu código de acceso a ZAVIA es: ${code}\n\nIngresa a ${window.location.origin} y pega el código.`);
    return `https://wa.me/${cleaned}?text=${msg}`;
  };

  const deleteCode = (code) => {
    setConfirm({
      title: "Eliminar código",
      description: `¿Eliminar el código ${code}? No podrá usarse para registrarse.`,
      confirmLabel: "Eliminar",
      onConfirm: async () => {
        try { await api.delete(`/admin/invite-codes/${code}`); toast.success("Código eliminado"); load(); }
        catch (err) { toast.error(err.response?.data?.detail || "Error"); }
      },
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Código copiado");
  };

  const revokeUser = (u) => {
    setConfirm({
      title: "Revocar usuario",
      description: `¿Revocar el acceso de ${u.email}? Se eliminará su cuenta de acceso.`,
      confirmLabel: "Revocar",
      onConfirm: async () => {
        try { await api.delete(`/admin/users/${u.user_id}`); toast.success("Usuario revocado"); load(); }
        catch (err) { toast.error(err.response?.data?.detail || "Error"); }
      },
    });
  };

  const toggleSuspend = (u) => {
    const willSuspend = !u.suspended;
    const verb = willSuspend ? "Suspender" : "Reactivar";
    setConfirm({
      title: `${verb} usuario`,
      description: `¿Seguro que deseas ${verb.toLowerCase()} a ${u.email}?${willSuspend ? " No tendrá acceso a nada mientras esté suspendido." : ""}`,
      confirmLabel: verb,
      onConfirm: async () => {
        try {
          await api.post(`/admin/users/${u.user_id}/suspend`, { suspend: willSuspend });
          toast.success(willSuspend ? "Usuario suspendido" : "Usuario reactivado"); load();
        } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
      },
    });
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-page">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-3 flex items-center gap-2">
          <span className="w-8 h-px bg-navy" /> Control de acceso
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy">Acceso</h1>
        <p className="text-sm text-nardo-500 mt-2">Solo los correos en esta lista o quienes tengan un código de invitación válido pueden entrar al sistema.</p>
      </div>

      {/* Business Insights */}
      {insights && (
        <div className="mb-8" data-testid="insights-section">
          <div className="text-[10px] uppercase tracking-[0.3em] text-nardo-500 mb-4">Negocio</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="bg-white border border-nardo-200 rounded-md p-5 relative">
              <div className="absolute top-0 left-0 h-[2px] w-12 bg-emerald-500" />
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-nardo-500">Licencias activas</span>
                <Building2 className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="font-display font-extrabold text-3xl text-navy" data-testid="stat-active">{insights.active_licenses}</div>
            </div>
            <div className="bg-white border border-nardo-200 rounded-md p-5 relative">
              <div className="absolute top-0 left-0 h-[2px] w-12 bg-amber-500" />
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-nardo-500">Vencen 30 días</span>
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div className="font-display font-extrabold text-3xl text-navy" data-testid="stat-expiring">{insights.expiring_soon.length}</div>
            </div>
            <div className="bg-white border border-nardo-200 rounded-md p-5 relative">
              <div className="absolute top-0 left-0 h-[2px] w-12 bg-red-500" />
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-nardo-500">Vencidas</span>
                <AlertTriangle className="w-4 h-4 text-red-600" />
              </div>
              <div className="font-display font-extrabold text-3xl text-navy" data-testid="stat-expired">{insights.expired_licenses}</div>
            </div>
            <div className="bg-white border border-nardo-200 rounded-md p-5 relative">
              <div className="absolute top-0 left-0 h-[2px] w-12 bg-navy" />
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-nardo-500">Códigos libres</span>
                <KeyRound className="w-4 h-4 text-navy" />
              </div>
              <div className="font-display font-extrabold text-3xl text-navy" data-testid="stat-codes-unused">{insights.codes_unused}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-nardo-200 rounded-md p-6 relative">
              <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-navy" />
                <h3 className="font-display font-semibold text-lg text-navy">Altas por mes</h3>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={insights.signups_by_month} margin={{ left: -20 }}>
                    <CartesianGrid stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="month" stroke="#7A7D80" fontSize={11} />
                    <YAxis stroke="#7A7D80" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ border: "1px solid #C9CBCD", borderRadius: 6 }} />
                    <Bar dataKey="signups" fill="#112240" radius={[2,2,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-nardo-200 rounded-md p-6 relative">
              <div className="absolute top-0 left-0 h-[2px] w-16 bg-amber-500" />
              <h3 className="font-display font-semibold text-lg text-navy mb-4">Próximos vencimientos</h3>
              <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="expiring-list">
                {insights.expiring_soon.length === 0 && (
                  <div className="text-sm text-nardo-500 text-center py-6">Nada que renovar pronto.</div>
                )}
                {insights.expiring_soon.map((e) => (
                  <div key={e.email} className="border border-amber-200 bg-amber-50/40 rounded-md p-2.5">
                    <div className="text-sm font-medium text-navy">{e.school_name || e.email}</div>
                    <div className="text-[11px] text-nardo-600 font-mono">{new Date(e.expires_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <NewSchoolCard onCreated={load} />

      <details className="group mb-2" data-testid="advanced-options">
      <summary className="cursor-pointer select-none text-xs uppercase tracking-[0.2em] text-nardo-500 hover:text-navy py-2">Opciones avanzadas (correos autorizados)</summary>
      <div className="grid grid-cols-1 gap-6 mt-3 max-w-3xl">
        {/* Correos autorizados */}
        <div className="bg-white border border-nardo-200 rounded-md p-6 relative">
          <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4 text-navy" />
            <h2 className="font-display font-semibold text-xl text-navy">Correos autorizados</h2>
          </div>
          <p className="text-xs text-nardo-500 mb-5">Agrega los correos Google que podrán iniciar sesión.</p>

          <form onSubmit={addEmail} className="space-y-3 mb-6">
            <input
              data-testid="allow-email-input"
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="correo@ejemplo.com"
              className="w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy"
            />
            <input
              data-testid="allow-email-note"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Nota (opcional): rol, área…"
              className="w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-sm"
            />
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Plan a asignar</label>
              <select
                data-testid="allow-email-plan"
                value={newPlan}
                onChange={(e) => setNewPlan(e.target.value)}
                className="w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy"
              >
                {plans.map((p) => (
                  <option key={p.key} value={p.key}>{p.name} · {p.registros} · ${Number(p.mensual).toLocaleString()}/mes</option>
                ))}
              </select>
              <p className="text-[11px] text-nardo-400 mt-1">Al elegir el plan se aplica automáticamente su límite de registros (alumnos + docentes). El sistema impide pasarse de ese número.</p>
            </div>

            <button data-testid="allow-email-add" type="submit" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-md text-sm">
              <Plus className="w-4 h-4" /> Autorizar
            </button>
          </form>

          <div className="space-y-2">
            {emails.length === 0 && <div className="text-sm text-nardo-500 text-center py-6">Aún no hay correos autorizados.</div>}
            {emails.map((e) => {
              const planKey = e.plan || users.find((u) => u.email === e.email)?.plan;
              const planName = plans.find((p) => p.key === planKey)?.name || planKey;
              return (
              <div key={e.email} className="flex items-center justify-between border border-nardo-100 rounded-md p-3" data-testid="allowed-email-row">
                <div className="min-w-0">
                  <div className="text-sm text-navy font-medium truncate">{e.email}</div>
                  {e.note && <div className="text-[11px] text-nardo-500">{e.note}</div>}
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {planKey ? (
                      <span className="inline-block text-[10px] uppercase tracking-[0.15em] bg-navy text-white px-2 py-0.5 rounded" data-testid={`allowed-email-plan-${e.email}`}>
                        {planName}
                      </span>
                    ) : (
                      <span className="inline-block text-[10px] uppercase tracking-[0.15em] bg-navy/5 text-navy px-2 py-0.5 rounded" data-testid={`allowed-email-max-${e.email}`}>
                        {(e.max_registrations ?? 1800).toLocaleString()} registros
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => removeEmail(e.email)} className="p-2 text-nardo-500 hover:text-destructive shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              );
            })}
          </div>
        </div>
      </div>
      </details>

      {/* Usuarios actuales */}
      <div className="bg-white border border-nardo-200 rounded-md mt-6">
        <div className="px-6 py-4 border-b border-nardo-200 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-navy" />
          <h2 className="font-display font-semibold text-xl text-navy">Usuarios con acceso</h2>
        </div>
        <div className="divide-y divide-nardo-100">
          {users.filter((u) => u.role !== "staff" && u.role !== "docente").map((u) => {
            const exts = users.filter((s) => (s.role === "staff" || s.role === "docente") && s.parent_user_id === u.user_id);
            return (
            <div key={u.user_id} className="px-4 sm:px-6 py-4">
              <div className="flex flex-col gap-3 md:grid md:grid-cols-12 md:gap-4 md:items-center text-sm">
              <div className="md:col-span-4 min-w-0">
                <div className="text-navy font-medium truncate">{u.name || u.email}</div>
                <div className="text-xs text-nardo-500 truncate">{u.email}</div>
              </div>
              <div className="md:col-span-3 text-nardo-600">
                <span className="md:hidden text-[10px] uppercase tracking-[0.2em] text-nardo-400 mr-1">Colegio:</span>
                {u.school_name || "—"}
                {u.expires_at && u.role !== "owner" && (
                  <div className="text-[10px] text-nardo-500">Vence: {new Date(u.expires_at).toLocaleDateString()}</div>
                )}
                {u.role !== "owner" && (
                  <div className="text-[10px] text-nardo-400">
                    {Number(u.sub_amount || 0) > 0
                      ? `Suscripción: $${Number(u.sub_amount).toLocaleString("es-MX")} / ${u.sub_days || 365} días`
                      : "Suscripción: sin precio"}
                  </div>
                )}
                {u.role !== "owner" && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <Package className="w-3 h-3 text-navy shrink-0" />
                    <select value={u.plan || ""} onChange={(e) => setUserPlan(u, e.target.value)} data-testid={`plan-select-${u.user_id}`}
                      className="text-[11px] border border-navy/30 rounded-md px-1.5 py-1 text-navy bg-white outline-none focus:border-navy max-w-[160px]">
                      <option value="">Elegir paquete…</option>
                      {plans.map((p) => <option key={p.key} value={p.key}>{p.name} · {p.registros}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="md:col-span-2">
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded ${u.role === "owner" ? "bg-navy text-white" : u.suspended ? "bg-red-100 text-red-700" : "bg-nardo-100 text-nardo-600"}`}>
                  {u.role === "owner" ? "Propietario" : u.suspended ? "Suspendido" : "Miembro"}
                </span>
              </div>
              <div className="md:col-span-3 flex flex-wrap gap-2 md:justify-end">
                {u.role !== "owner" && (
                  <>
                    <button onClick={() => setModsEdit({ user_id: u.user_id, email: u.email, portals: portalsOf(u.modules) })} className="text-[11px] text-navy hover:text-white hover:bg-navy border border-navy/30 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`modules-${u.user_id}`}>
                      <LayoutGrid className="w-3 h-3" /> Portales {Array.isArray(u.modules) ? `${portalsOf(u.modules).length}/${PORTALS.length}` : "· Todos"}
                    </button>
                    <button onClick={() => extendLicense(u)} className="text-[11px] text-navy hover:text-white hover:bg-navy border border-navy/30 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`extend-${u.user_id}`}>
                      <RefreshCw className="w-3 h-3" /> Renovar
                    </button>
                    <button onClick={() => setSubEdit({ user_id: u.user_id, email: u.email, amount: u.sub_amount || "", days: u.sub_days || 365 })} className="text-[11px] text-navy hover:text-white hover:bg-navy border border-navy/30 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`sub-price-${u.user_id}`}>
                      <CreditCard className="w-3 h-3" /> Precio
                    </button>
                    <button onClick={() => setContractFor({ user_id: u.user_id, email: u.email, colegio: u.school_name || "", responsable: "", plan: u.plan || "base" })} className="text-[11px] text-emerald-700 hover:text-white hover:bg-emerald-600 border border-emerald-300 hover:border-emerald-600 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`contract-${u.user_id}`}>
                      <FileText className="w-3 h-3" /> Contrato
                    </button>
                    <button onClick={() => toggleSuspend(u)} className={`text-[11px] border rounded-md px-2.5 py-1.5 inline-flex items-center gap-1 transition-colors ${u.suspended ? "text-emerald-700 border-emerald-300 hover:bg-emerald-600 hover:text-white hover:border-emerald-600" : "text-amber-700 border-amber-300 hover:bg-amber-500 hover:text-white hover:border-amber-500"}`} data-testid={`suspend-${u.user_id}`}>
                      <Ban className="w-3 h-3" /> {u.suspended ? "Reactivar" : "Suspender"}
                    </button>
                    <button onClick={() => revokeUser(u)} className="text-[11px] text-nardo-600 hover:text-white hover:bg-destructive border border-nardo-200 hover:border-destructive rounded-md px-2.5 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`revoke-${u.user_id}`}>
                      <X className="w-3 h-3" /> Revocar
                    </button>
                  </>
                )}
              </div>
              </div>
              {exts.length > 0 && (
                <div className="mt-3 ml-1 border-l-2 border-nardo-200 pl-3" data-testid={`ext-emails-${u.user_id}`}>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-400 mb-1">Extensión de correos</div>
                  <div className="flex flex-col gap-1">
                    {exts.map((s) => (
                      <div key={s.user_id} className="text-[11px] text-nardo-500 flex flex-wrap items-center gap-x-2" data-testid={`ext-email-${s.user_id}`}>
                        <span className="text-nardo-600">{s.name || (s.role === "docente" ? "Docente" : "Administrativo")}</span>
                        <span className="text-[9px] uppercase tracking-wider bg-navy/5 text-navy px-1.5 py-0.5 rounded">{s.role === "docente" ? "Docente" : "Admin"}</span>
                        <span className="truncate">{s.email}</span>
                        {s.suspended && <span className="text-red-500">· suspendido</span>}
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] text-nardo-400 mt-1">Ligados a este correo · se bloquean al suspender o vencer la licencia.</div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {/* Cobros de suscripción */}
      <div className="bg-white border border-nardo-200 rounded-md mt-6" data-testid="sub-revenue-section">
        <div className="px-6 py-4 border-b border-nardo-200 flex items-center gap-2 flex-wrap">
          <CreditCard className="w-4 h-4 text-navy" />
          <h2 className="font-display font-semibold text-xl text-navy">Cobros de suscripción</h2>
          <button onClick={exportSubCsv} data-testid="export-sub-csv" className="ml-auto text-[11px] text-navy hover:text-white hover:bg-navy border border-navy/30 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1 transition-colors">
            <Copy className="w-3 h-3" /> Exportar CSV
          </button>
        </div>
        {(() => {
          const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));
          const now = new Date();
          const mk = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`; };
          const curKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
          const totalAll = subPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
          const totalMonth = subPayments.filter((p) => mk(p.created_at) === curKey).reduce((s, p) => s + Number(p.amount || 0), 0);
          const groups = {};
          subPayments.forEach((p) => { const k = mk(p.created_at); (groups[k] = groups[k] || []).push(p); });
          const keys = Object.keys(groups).sort().reverse();
          const monthName = (k) => { const [y, m] = k.split("-"); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("es-MX", { month: "long", year: "numeric" }); };
          return (
            <>
              <div className="grid grid-cols-2 gap-4 px-6 py-4 border-b border-nardo-100">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-nardo-400">Este mes</div>
                  <div className="text-2xl font-display font-bold text-navy" data-testid="sub-total-month">{fmt(totalMonth)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-nardo-400">Total recaudado</div>
                  <div className="text-2xl font-display font-bold text-navy" data-testid="sub-total-all">{fmt(totalAll)}</div>
                </div>
              </div>
              <div className="divide-y divide-nardo-100 max-h-96 overflow-y-auto">
                {subPayments.length === 0 && <div className="px-6 py-8 text-center text-sm text-nardo-500">Aún no hay cobros de suscripción</div>}
                {keys.map((k) => (
                  <div key={k}>
                    <div className="px-6 py-2 bg-nardo-50 text-[11px] uppercase tracking-[0.2em] text-nardo-500 flex items-center justify-between">
                      <span>{monthName(k)}</span>
                      <span className="text-navy font-semibold">{fmt(groups[k].reduce((s, p) => s + Number(p.amount || 0), 0))}</span>
                    </div>
                    {groups[k].map((p) => (
                      <div key={p.id} className="px-6 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`sub-payment-${p.id}`}>
                        <div className="min-w-0">
                          <div className="text-navy font-medium truncate">{p.school_name || p.email || "Colegio"}</div>
                          <div className="text-[11px] text-nardo-500">{new Date(p.created_at).toLocaleString("es-MX")} · {p.method || "Tarjeta"} · {p.days || 365} días</div>
                        </div>
                        <div className="text-navy font-semibold shrink-0">{fmt(p.amount)}</div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          );
        })()}
      </div>

      {/* Intentos rechazados */}
      <div className="bg-white border border-nardo-200 rounded-md mt-6" data-testid="attempts-section">
        <div className="px-6 py-4 border-b border-nardo-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-navy" />
          <h2 className="font-display font-semibold text-xl text-navy">Intentos rechazados</h2>
          <span className="text-xs text-nardo-500">(últimos {attempts.length})</span>
        </div>
        <div className="divide-y divide-nardo-100 max-h-80 overflow-y-auto">
          {attempts.length === 0 && <div className="px-6 py-8 text-center text-sm text-nardo-500">Sin intentos rechazados</div>}
          {attempts.map((a, i) => (
            <div key={i} className="px-6 py-2 grid grid-cols-12 gap-4 text-xs items-center">
              <div className="col-span-3 font-mono text-nardo-600">{new Date(a.timestamp).toLocaleString()}</div>
              <div className="col-span-4 text-navy">{a.email || <span className="text-nardo-400">—</span>}</div>
              <div className="col-span-3 font-mono text-nardo-600">{a.code || "—"}</div>
              <div className="col-span-2 text-red-600 uppercase tracking-[0.15em] text-[10px]">{a.reason}</div>
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-accept" onClick={runConfirm} className="bg-navy hover:bg-navy-light">
              {confirm?.confirmLabel || "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!modsEdit} onOpenChange={(o) => { if (!o) setModsEdit(null); }}>
        <DialogContent data-testid="modules-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Portales de {modsEdit?.email}</DialogTitle></DialogHeader>
          {modsEdit && <PortalPicker value={modsEdit.portals} onChange={(portals) => setModsEdit({ ...modsEdit, portals })} testid="edit-portal" />}
          <button data-testid="modules-save" onClick={saveModules} className="w-full bg-navy text-white rounded-md py-2.5 text-sm font-semibold">Guardar portales</button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!subEdit} onOpenChange={(o) => { if (!o) setSubEdit(null); }}>
        <AlertDialogContent data-testid="sub-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Precio de suscripción</AlertDialogTitle>
            <AlertDialogDescription>{subEdit?.email} — define el monto y el periodo que este colegio pagará con tarjeta para renovar.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Monto (MXN)</label>
              <input type="number" min="0" step="1" value={subEdit?.amount ?? ""} onChange={(e) => setSubEdit((s) => ({ ...s, amount: e.target.value }))} data-testid="sub-amount-input" className="w-full mt-1 border border-nardo-200 rounded-md px-3 py-2 text-sm" placeholder="0.00" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Periodo (días)</label>
              <input type="number" min="1" step="1" value={subEdit?.days ?? 365} onChange={(e) => setSubEdit((s) => ({ ...s, days: e.target.value }))} data-testid="sub-days-input" className="w-full mt-1 border border-nardo-200 rounded-md px-3 py-2 text-sm" placeholder="365" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sub-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="sub-save" onClick={saveSubscription} className="bg-navy hover:bg-navy-light">Guardar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!contractFor} onOpenChange={(o) => { if (!o) setContractFor(null); }}>
        <AlertDialogContent data-testid="contract-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Descargar contrato</AlertDialogTitle>
            <AlertDialogDescription>Contrato anual ZAVIA listo para imprimir. Solo confirma el paquete, el nombre del colegio y el responsable de pago.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Paquete / Plan</label>
              <select value={contractFor?.plan || "base"} onChange={(e) => setContractFor((s) => ({ ...s, plan: e.target.value }))} data-testid="contract-plan" className="w-full mt-1 border border-nardo-200 rounded-md px-3 py-2 text-sm bg-white">
                {plans.map((p) => <option key={p.key} value={p.key}>{p.name} — {p.registros} · ${Number(p.mensual).toLocaleString("es-MX")}/mes</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Nombre del colegio</label>
              <input value={contractFor?.colegio ?? ""} onChange={(e) => setContractFor((s) => ({ ...s, colegio: e.target.value }))} data-testid="contract-colegio" className="w-full mt-1 border border-nardo-200 rounded-md px-3 py-2 text-sm" placeholder="Colegio Pitágoras" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Responsable de pago</label>
              <input value={contractFor?.responsable ?? ""} onChange={(e) => setContractFor((s) => ({ ...s, responsable: e.target.value }))} data-testid="contract-responsable" className="w-full mt-1 border border-nardo-200 rounded-md px-3 py-2 text-sm" placeholder="Nombre completo del responsable" />
            </div>
            {contractHistory.length > 0 && (
              <div className="border-t border-nardo-100 pt-2" data-testid="contract-history">
                <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 mb-1">Historial de contratos ({contractHistory.length})</div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {contractHistory.map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-2 text-xs bg-nardo-50 rounded-md px-2 py-1.5" data-testid={`contract-hist-${c.id}`}>
                      <span className="min-w-0 truncate text-nardo-600">
                        <b className="text-navy">{c.plan_name}</b> · {new Date(c.created_at).toLocaleDateString("es-MX")} · {c.responsable}
                        {c.status === "firmado"
                          ? <span className="ml-1 text-emerald-700 font-medium">· Firmado ✓</span>
                          : <span className="ml-1 text-amber-600 font-medium">· Pendiente</span>}
                      </span>
                      <button onClick={() => redownloadContract(c)} data-testid={`contract-redownload-${c.id}`} className="shrink-0 inline-flex items-center gap-1 text-emerald-700 hover:text-white hover:bg-emerald-600 border border-emerald-300 hover:border-emerald-600 rounded px-2 py-1 transition-colors">
                        <FileText className="w-3 h-3" /> PDF
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="contract-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="contract-download" onClick={(e) => { e.preventDefault(); downloadContract(); }} disabled={savingContract} className="bg-emerald-600 hover:bg-emerald-700">{savingContract ? "Generando…" : "Descargar PDF"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
